import { createTheme, type PaletteMode } from '@mui/material/styles';
import { createComponentOverrides } from '@/theme/componentOverrides';
import { palette } from '@/theme/palette';
import { typography } from '@/theme/typography';
import { fontFamilyOptions, fontScaleOptions } from '@/theme/themeStudioConfig';
import type {
  ThemeStudioDensity,
  ThemeStudioFontFamily,
  ThemeStudioFontScale,
} from '@/shell/AppShellContext';

interface ThemeOverrides {
  primaryBrandColor?: string;
  secondaryBrandColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  borderRadius?: number;
  density?: ThemeStudioDensity;
  fontFamily?: ThemeStudioFontFamily;
  fontScale?: ThemeStudioFontScale;
}

function buildPalette(mode: PaletteMode) {
  if (mode === 'dark') {
    return {
      ...palette,
      mode,
      background: {
        default: '#0f1724',
        paper: '#172033',
      },
      text: {
        primary: '#f8fafc',
        secondary: '#c7d2e2',
      },
      divider: 'rgba(148, 163, 184, 0.28)',
      action: {
        hover: 'rgba(148, 163, 184, 0.12)',
        selected: 'rgba(99, 102, 241, 0.18)',
      },
    };
  }

  return {
    ...palette,
    mode,
  };
}

export function createAppTheme(
  mode: PaletteMode = 'light',
  overrides: ThemeOverrides = {},
) {
  const basePalette = buildPalette(mode);

  const paletteWithOverrides = {
    ...basePalette,
    primary: overrides.primaryBrandColor
      ? {
          ...basePalette.primary,
          main: overrides.primaryBrandColor,
        }
      : basePalette.primary,
    secondary: overrides.secondaryBrandColor
      ? {
          ...basePalette.secondary,
          main: overrides.secondaryBrandColor,
        }
      : basePalette.secondary,
    background:
      overrides.backgroundColor || overrides.surfaceColor
        ? {
            ...basePalette.background,
            default:
              overrides.backgroundColor ?? basePalette.background?.default,
            paper: overrides.surfaceColor ?? basePalette.background?.paper,
          }
        : basePalette.background,
  };

  const resolvedFontFamily =
    fontFamilyOptions.find((option) => option.id === overrides.fontFamily)
      ?.family ?? typography.fontFamily;

  const resolvedFontScale =
    fontScaleOptions.find((option) => option.id === overrides.fontScale)
      ?.scale ?? 1;

  const spacingMultiplier =
    overrides.density === 'compact'
      ? 0.875
      : overrides.density === 'spacious'
        ? 1.15
        : 1;

  const baseRadius = overrides.borderRadius ?? 8;

  return createTheme({
    palette: paletteWithOverrides,
    typography: {
      ...typography,
      fontFamily: resolvedFontFamily,
      htmlFontSize: 16 * resolvedFontScale,
    },
    spacing: 8 * spacingMultiplier,
    shape: {
      borderRadius: baseRadius,
    },
    components: createComponentOverrides(),
  });
}

export const theme = createAppTheme('light');
